import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Action, Store } from '@ngrx/store';
import * as fromRoot from 'app/core/store';
import { OrderDetailBackToOverviewAction } from 'app/core/store/order-detail/order-detail.actions';
import { OrderDetail, FlorderDetailState, HasRecurrenceDates } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { DialogData, OrdersRecurrenceModalComponent } from 'app/modules/order/orders-recurrence-modal/orders-recurrence-modal.component';
import * as RouterActions from '../store/router/router.actions';
import { map } from 'rxjs/operators';
import { AzureMonitoringService } from './AzureMonitoringService';

@Injectable({ providedIn: 'root' })
export class RecurrenceService {
  private loadingDates: string[];
  private recurrenceDialog?: MatDialogRef<OrdersRecurrenceModalComponent>;

  constructor(private store: Store<fromRoot.RootState>, private dialog: MatDialog, private azureMonitoringService: AzureMonitoringService) {
  }

  closeRecurrenceDatesDialog() {
    this.recurrenceDialog?.close();
  }

  openRecurrenceDatesDialog({
                              statePath,
                              cb,
                              goAction,
                              backToConfirmAction,
                              createButtonLabel,
                              gotoOverviewUrl,
                            }: {
    statePath: 'editOrderDetail';
    cb: (loadingDates?: string[]) => void;
    goAction: (florder: OrderDetail) => Action;
    backToConfirmAction: Action;
    createButtonLabel: string;
    gotoOverviewUrl: string;
  }) {
    const createRecurrenceOrders = (dates => {
      this.loadingDates = dates;
      cb(dates);
    }).bind(this);

    const gotoOverview = (() => {
      const overviewState: FlorderDetailState = 'view';
      this.store.dispatch(RouterActions.goAction({ path: gotoOverviewUrl }));
      // Added an extra action, due to the workings of the dialog in angular material beta 2.0.0 v6
      // when a dialog is closed, the detection of a closed dialog is handled slowly
      // This makes the 'if' always true in order-edit.container.ts file on line 151 and thus opening a new dialog every time
      // To solve this we dispatch a new action to set the editOrderDetail.mode.state === 'view' instead of 'recurrence-dates'
      this.azureMonitoringService.logEvent('Click on "back to overview from recurrence', {
        currentUrl: document.URL,
        overviewState,
      });
      this.store.dispatch(new OrderDetailBackToOverviewAction(overviewState));
      this.closeRecurrenceDatesDialog();
    }).bind(this);

    const openOrder = (order => {
      this.store.dispatch(goAction(order));
      this.closeRecurrenceDatesDialog();
    }).bind(this);

    const backToCreateConfirm = (() => {
      this.store.dispatch(backToConfirmAction);
      this.loadingDates = null;
      this.closeRecurrenceDatesDialog();
    }).bind(this);

    const dates$ = this.store.select(statePath).pipe(map((hasRecurrenceDates: HasRecurrenceDates) => hasRecurrenceDates.recurrenceDates));

    const config: MatDialogConfig = {
      disableClose: true,
      width: '30%',
      height: 'auto',
      data: {
        dates$,
        createRecurrenceOrders,
        gotoOverview,
        openOrder,
        backToCreateConfirm,
        createButtonLabel,
      } as DialogData,
      role: 'dialog',
    };
    this.recurrenceDialog = this.dialog.open(OrdersRecurrenceModalComponent, config);
    this.recurrenceDialog.afterClosed().subscribe(result => {
      this.recurrenceDialog = null;
    });
  }

  isOpen() {
    return !!this.recurrenceDialog;
  }
}
